<template>
    <kt-modal 
        :visible="visible" 
        title="Select Contact"
        :size="'modal-md'" 
        @dismiss="$emit('close')"
    >
        <template #body>
            <div v-if="isLoading" class="text-center py-4">
                <i class="fas fa-spinner fa-spin fa-2x"></i>
                <p class="mt-2">Loading staff...</p>
            </div>
            <div v-else>
                <div v-if="staffList.length === 0" class="text-center py-4 text-muted">
                    No staff members found for this client.
                </div>
                <div v-else class="list-group">
                    <button 
                        v-for="staff in staffList" 
                        :key="staff.Id"
                        type="button"
                        class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                        @click="selectStaff(staff)"
                    >
                        <div>
                            <strong>{{ staff.Name || `${staff.FirstName || ''} ${staff.LastName || ''}`.trim() }}</strong>
                            <div class="small text-muted">
                                <span v-if="staff.Email">{{ staff.Email }}</span>
                                <span v-if="staff.Email && staff.Phone"> | </span>
                                <span v-if="staff.Phone">{{ staff.Phone }}</span>
                            </div>
                        </div>
                        <ChevronRight :size="16" class="text-muted" />
                    </button>
                </div>
            </div>
        </template>

        <template #footer>
            <button @click="showAddContact = true" class="btn btn-outline-primary me-auto">
                <Plus :size="16" class="me-1" />Add Contact
            </button>
            <button @click="$emit('close')" class="btn btn-outline-secondary">
                <X :size="16" class="me-1" />Cancel
            </button>
        </template>
    </kt-modal>

    <!-- Add Contact Sub-Modal -->
    <kt-modal
        :visible="showAddContact"
        title="Add Contact"
        :size="'modal-md'"
        @dismiss="dismissAddContact"
    >
        <template #body>
            <staff-form v-model="newStaffForm"></staff-form>
        </template>
        <template #footer>
            <button @click="dismissAddContact" class="btn btn-outline-secondary">
                <X :size="16" class="me-1" />Cancel
            </button>
            <button @click="saveNewContact" class="btn btn-primary">
                <Save :size="16" class="me-1" />Save
            </button>
        </template>
    </kt-modal>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import Api from '@/services/Api';
import KtModal from '@/components/UI/KtModal.vue';
import StaffForm from '@/components/Staff/StaffForm.vue';
import Alert from '@/services/Alert';
import { X, ChevronRight, Plus, Save } from 'lucide-vue-next';

const props = defineProps<{
    visible: boolean;
    clientId?: number | null;
}>();

const emit = defineEmits(['close', 'select']);

const staffList = ref<any[]>([]);
const isLoading = ref(false);
const showAddContact = ref(false);
const newStaffForm = ref<any>({});

const loadStaff = async () => {
    if (!props.clientId) {
        staffList.value = [];
        return;
    }

    isLoading.value = true;
    try {
        const response = await Api.loadStaff({ 
            CustomerNo: [props.clientId],
            Page: 0,
            Size: 999999999
        }, true);
        staffList.value = response ?? [];
    } catch (error) {
        console.error('Error loading staff:', error);
        staffList.value = [];
    } finally {
        isLoading.value = false;
    }
};

const selectStaff = (staff: any) => {
    emit('select', staff);
};

const dismissAddContact = () => {
    showAddContact.value = false;
    newStaffForm.value = {};
};

const saveNewContact = async () => {
    try {
        await Api.saveStaff(newStaffForm.value, true);
        Alert.notify({ text: 'Contact created successfully!', type: 'success' });
        dismissAddContact();
        await loadStaff();
    } catch (error) {
        console.error('Error saving contact:', error);
        Alert.notify({ text: 'Error saving contact', type: 'error' });
    }
};

watch(() => props.visible, (newVisible) => {
    if (newVisible && props.clientId) {
        loadStaff();
    }
}, { immediate: true });

watch(() => showAddContact.value, (val) => {
    if (val && props.clientId) {
        newStaffForm.value = { ClientMap: [props.clientId] };
    }
});

onMounted(() => {
    if (props.visible && props.clientId) {
        loadStaff();
    }
});
</script>
